import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { MdWarningAmber } from "react-icons/md";
import { emptyCompanyProfile } from "../societe/PageSocieteConfig";
import { personsApi, teamApi, tenantCompanyApi } from "../../services/apiClient";
import { ACTIVITIES, RC_TOOLS, TOOLS, TRAITEMENTS } from "./hapivotOnboardingConstants";
import { SECTEUR_ACTIVITE_OPTIONS } from "../../shared/secteurActiviteOptions";

const BG = "#f5f4f0";
const CARD = "#fff";
const BORDER = "#e0dfd8";
const PURPLE = "#534AB7";

function ProgressBar({ step, total }) {
  const pct = Math.round((step / total) * 100);
  return (
    <div style={{ height: 3, background: BORDER, borderRadius: 2, marginBottom: "2rem" }}>
      <div style={{ height: "100%", width: `${pct}%`, background: PURPLE, borderRadius: 2, transition: "width 0.35s ease" }} />
    </div>
  );
}

export default function HapivotOnboardingWizard({ currentUser, organizationName, existingCompanyProfile, onCompleted, onToast }) {
  const totalSteps = 5;
  const [step, setStep] = useState(1);

  const baseProfile = useMemo(
    () => ({ ...emptyCompanyProfile, ...(existingCompanyProfile && typeof existingCompanyProfile === "object" ? existingCompanyProfile : {}) }),
    [existingCompanyProfile]
  );

  const [raisonSociale, setRaisonSociale] = useState(baseProfile.raisonSociale || organizationName || "");
  const [sirenSiret, setSirenSiret] = useState(baseProfile.sirenSiret || "");
  const [secteur, setSecteur] = useState(baseProfile.secteurActivite || "");
  const secteurActiviteOptions = SECTEUR_ACTIVITE_OPTIONS;
  const [activite, setActivite] = useState(baseProfile.activite || "");
  const [dpoEmail, setDpoEmail] = useState(baseProfile.contactDpo || "");

  const [selectedActivities, setSelectedActivities] = useState([]);
  const [selectedTools, setSelectedTools] = useState({});

  const [responsableRows, setResponsableRows] = useState([
    { role: "Point de contact RGPD / DPO", email: "" },
    { role: "Référent métier (traitements)", email: "" },
    { role: "Référent SI / sécurité", email: "" },
  ]);

  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const emails = new Set();
      if (currentUser?.email) emails.add(String(currentUser.email).trim().toLowerCase());
      try {
        const [personList, team] = await Promise.all([personsApi.list(), teamApi.dashboard()]);
        if (cancelled) return;
        (Array.isArray(personList) ? personList : []).forEach((p) => {
          if (p?.email && String(p.email).trim()) emails.add(String(p.email).trim().toLowerCase());
        });
        (team?.members || []).forEach((m) => {
          if (m?.email) emails.add(String(m.email).trim().toLowerCase());
        });
      } catch {
        /* liste optionnelle */
      }
      if (!cancelled) setEmailSuggestions([...emails].sort());
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  useEffect(() => {
    if (step !== 5) return;
    setResponsableRows((rows) => {
      const next = [...rows];
      if (dpoEmail.trim() && !next[0]?.email?.trim()) {
        next[0] = { ...next[0], email: dpoEmail.trim() };
      }
      return next;
    });
  }, [step, dpoEmail]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const toggleActivity = (id) => {
    setSelectedActivities((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleTool = (actId, opt, multi) => {
    setSelectedTools((prev) => {
      const cur = new Set(prev[actId] || []);
      if (multi) {
        if (cur.has(opt)) cur.delete(opt);
        else cur.add(opt);
      } else {
        cur.clear();
        cur.add(opt);
      }
      return { ...prev, [actId]: [...cur] };
    });
  };

  const validationHasRc = useMemo(() => {
    let has = false;
    selectedActivities.forEach((actId) => {
      const t = TRAITEMENTS[actId];
      if (t?.rc) has = true;
    });
    return has;
  }, [selectedActivities]);

  const step1Valid = raisonSociale.trim().length > 0 && sirenSiret.trim().length > 0;
  const step2Valid = selectedActivities.length > 0;

  const persistProfilePayload = useCallback(() => {
    const onboardingWizardState = JSON.stringify({
      selectedActivities,
      selectedTools,
      ts: Date.now(),
    });
    const responsablesConfigJson = JSON.stringify(
      responsableRows.map((r) => ({ role: r.role, email: (r.email || "").trim() })).filter((r) => r.email)
    );
    return {
      ...baseProfile,
      raisonSociale: raisonSociale.trim(),
      sirenSiret: sirenSiret.trim(),
      secteurActivite: secteur,
      activite: activite.trim(),
      contactDpo: dpoEmail.trim(),
      onboardingWizardState,
      responsablesConfigJson,
    };
  }, [baseProfile, raisonSociale, sirenSiret, secteur, activite, dpoEmail, selectedActivities, selectedTools, responsableRows]);

  const handleFinalize = async () => {
    setSubmitting(true);
    try {
      await tenantCompanyApi.completeOnboarding(persistProfilePayload());
      await onCompleted?.();
    } catch (e) {
      onToast?.("error", e?.message || "Enregistrement impossible.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "9px 12px",
    border: `0.5px solid #ccc`,
    borderRadius: 8,
    fontSize: 14,
    background: "#fff",
    outline: "none",
  };

  const btnBase = {
    fontSize: 14,
    padding: "10px 24px",
    borderRadius: 8,
    border: `0.5px solid #ccc`,
    background: "#fff",
    color: "#555",
    cursor: "pointer",
  };

  const btnPrimary = {
    ...btnBase,
    background: PURPLE,
    color: "#fff",
    borderColor: PURPLE,
  };

  const overlay = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="hapivot-onboarding-title"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2147483647,
        isolation: "isolate",
        pointerEvents: "auto",
        fontFamily: 'system-ui, -apple-system, sans-serif',
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(15, 23, 42, 0.92)",
          backdropFilter: "blur(8px)",
        }}
      />
      <div
        style={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          padding: "24px 16px",
          paddingBottom: 48,
          overflowY: "auto",
          overflowX: "hidden",
          boxSizing: "border-box",
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
          zIndex: 1,
        }}
      >
      <div
        style={{
          width: "100%",
          maxWidth: 620,
          marginBottom: 24,
          background: "#ffffff",
          borderRadius: 16,
          padding: "22px 22px 28px",
          boxShadow: "0 25px 60px rgba(0, 0, 0, 0.45)",
          border: `1px solid ${BORDER}`,
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            background: BG,
            borderRadius: 12,
            padding: "1.25rem 1.35rem",
            border: `0.5px solid ${BORDER}`,
            marginBottom: 12,
            fontSize: 13,
            color: "#854F0B",
            backgroundColor: "#FAEEDA",
            borderColor: "#EF9F27",
            lineHeight: 1.55,
          }}
        >
          <strong>Finalisez la configuration</strong> de l’organisation « {organizationName || "votre structure"} » pour utiliser la plateforme. Ce formulaire s’affiche à la première connexion ou lorsque la configuration n’a pas encore été enregistrée.
        </div>

        <ProgressBar step={step} total={totalSteps} />

        {/* Étape 1 */}
        {step === 1 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.08em", color: "#aaa", marginBottom: 8 }}>
              Étape 1 sur {totalSteps}
            </div>
            <h1 id="hapivot-onboarding-title" style={{ fontSize: 22, fontWeight: 500, marginBottom: 6 }}>
              Parlez-nous de votre entreprise
            </h1>
            <p style={{ fontSize: 14, color: "#666", marginBottom: "2rem", lineHeight: 1.5 }}>
              Ces informations figureront en en-tête de votre registre des traitements.
            </p>
            <div style={{ background: CARD, border: `0.5px solid ${BORDER}`, borderRadius: 12, padding: "1.25rem", marginBottom: 12 }}>
              <label style={{ fontSize: 13, color: "#555", marginBottom: 6, display: "block" }}>Raison sociale *</label>
              <input type="text" value={raisonSociale} onChange={(e) => setRaisonSociale(e.target.value)} placeholder="Ex : Belle & Bio SAS" style={{ ...inputStyle, marginBottom: "1rem" }} />
              <label style={{ fontSize: 13, color: "#555", marginBottom: 6, display: "block" }}>SIRET *</label>
              <input type="text" value={sirenSiret} onChange={(e) => setSirenSiret(e.target.value)} placeholder="14 chiffres (ex. 123 456 789 01234)" style={{ ...inputStyle, marginBottom: "1rem" }} />
              <label style={{ fontSize: 13, color: "#555", marginBottom: 6, display: "block" }}>Secteur d&apos;activité</label>
              <select value={secteur} onChange={(e) => setSecteur(e.target.value)} style={{ ...inputStyle, marginBottom: "1rem" }}>
                {secteurActiviteOptions.map((o) => (
                  <option key={o.value || "__empty"} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <label style={{ fontSize: 13, color: "#555", marginBottom: 6, display: "block" }}>Activité de l&apos;entreprise (optionnel)</label>
              <textarea
                value={activite}
                onChange={(e) => setActivite(e.target.value)}
                placeholder="Décrivez brièvement votre activité (libre, non obligatoire à cette étape)."
                rows={3}
                style={{ ...inputStyle, marginBottom: "1rem", resize: "vertical", minHeight: 72 }}
              />
              <label style={{ fontSize: 13, color: "#555", marginBottom: 6, display: "block" }}>Email du DPO ou responsable RGPD</label>
              <input type="email" value={dpoEmail} onChange={(e) => setDpoEmail(e.target.value)} placeholder="dpo@votreentreprise.fr" style={inputStyle} list="hapivot-email-suggestions" />
              <datalist id="hapivot-email-suggestions">
                {emailSuggestions.map((em) => (
                  <option key={em} value={em} />
                ))}
              </datalist>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: "1.5rem" }}>
              <button type="button" disabled={!step1Valid} onClick={() => setStep(2)} style={{ ...btnPrimary, opacity: step1Valid ? 1 : 0.45, cursor: step1Valid ? "pointer" : "not-allowed" }}>
                Continuer →
              </button>
            </div>
          </div>
        )}

        {/* Étape 2 */}
        {step === 2 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.08em", color: "#aaa", marginBottom: 8 }}>
              Étape 2 sur {totalSteps}
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 500, marginBottom: 6 }}>Quelles activités faites-vous ?</h2>
            <p style={{ fontSize: 14, color: "#666", marginBottom: "2rem", lineHeight: 1.5 }}>
              Sélectionnez tout ce qui correspond. Vous pourrez affiner votre registre ensuite.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {ACTIVITIES.map((a) => {
                const sel = selectedActivities.includes(a.id);
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => toggleActivity(a.id)}
                    style={{
                      textAlign: "left",
                      background: sel ? "#EEEDFE" : CARD,
                      border: `0.5px solid ${sel ? PURPLE : BORDER}`,
                      borderRadius: 10,
                      padding: 14,
                      cursor: "pointer",
                      transition: "border-color 0.15s, background 0.15s",
                    }}
                  >
                    <div style={{ marginBottom: 6, display: "inline-flex", alignItems: "center", lineHeight: 1 }}>{a.icon}</div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "#1a1a1a" }}>{a.label}</div>
                    <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{a.sub}</div>
                  </button>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: "1.5rem" }}>
              <button type="button" onClick={() => setStep(1)} style={btnBase}>
                ← Retour
              </button>
              <button type="button" disabled={!step2Valid} onClick={() => setStep(3)} style={{ ...btnPrimary, opacity: step2Valid ? 1 : 0.45, cursor: step2Valid ? "pointer" : "not-allowed" }}>
                Continuer →
              </button>
            </div>
          </div>
        )}

        {/* Étape 3 outils */}
        {step === 3 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.08em", color: "#aaa", marginBottom: 8 }}>
              Étape 3 sur {totalSteps}
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 500, marginBottom: 6 }}>Quels outils utilisez-vous ?</h2>
            <p style={{ fontSize: 14, color: "#666", marginBottom: "2rem", lineHeight: 1.5 }}>Pour chaque activité sélectionnée, indiquez vos outils principaux.</p>
            {selectedActivities.map((actId) => {
              const t = TOOLS[actId];
              if (!t) return null;
              const sel = selectedTools[actId] || [];
              return (
                <div key={actId} style={{ marginBottom: "1.5rem" }}>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8, color: "#333", display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#1D9E75", flexShrink: 0 }} />
                    {t.label}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {t.opts.map((opt) => {
                      const active = sel.includes(opt);
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => toggleTool(actId, opt, !!t.multi)}
                          style={{
                            fontSize: 12,
                            padding: "6px 14px",
                            borderRadius: 999,
                            border: `0.5px solid ${active ? PURPLE : "#ccc"}`,
                            background: active ? PURPLE : "#fff",
                            color: active ? "#EEEDFE" : "#555",
                            cursor: "pointer",
                          }}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <div style={{ display: "flex", gap: 10, marginTop: "1.5rem" }}>
              <button type="button" onClick={() => setStep(2)} style={btnBase}>
                ← Retour
              </button>
              <button type="button" onClick={() => setStep(4)} style={btnPrimary}>
                Continuer →
              </button>
            </div>
          </div>
        )}

        {/* Étape 4 validation */}
        {step === 4 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.08em", color: "#aaa", marginBottom: 8 }}>
              Étape 4 sur {totalSteps}
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 500, marginBottom: 6 }}>Récapitulatif des traitements détectés</h2>
            <p style={{ fontSize: 14, color: "#666", marginBottom: "2rem", lineHeight: 1.5 }}>
              Aperçu basé sur vos réponses — vous pourrez modifier le registre ensuite dans l&apos;application.
            </p>
            {validationHasRc && (
              <div
                style={{
                  background: "#FAEEDA",
                  border: "0.5px solid #EF9F27",
                  borderRadius: 8,
                  padding: "10px 14px",
                  fontSize: 12,
                  color: "#854F0B",
                  marginBottom: "1.5rem",
                  lineHeight: 1.6,
                }}
              >
                Certains prestataires peuvent correspondre à des <strong>responsables conjoints (Art. 26)</strong> — vérifiez votre CMP et vos mentions.
              </div>
            )}
            {selectedActivities.map((actId) => {
              const tr = TRAITEMENTS[actId];
              const tools = selectedTools[actId] || [];
              if (!tr) return null;
              return (
                <div key={actId} style={{ marginBottom: "1.5rem" }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "#333", marginBottom: 10, paddingBottom: 6, borderBottom: `0.5px solid ${BORDER}`, display: "flex", alignItems: "center", gap: 8 }}>
                    {tr.nom}{" "}
                    <span style={{ fontSize: 11, background: "#EEEDFE", color: PURPLE, padding: "2px 8px", borderRadius: 999 }}>
                      {tools.length} prestataire{tools.length > 1 ? "s" : ""}
                    </span>
                  </div>
                  <div style={{ background: CARD, border: `0.5px solid ${BORDER}`, borderRadius: 10, padding: "12px 14px", display: "flex", gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 3 }}>{tr.nom}</div>
                      <div style={{ fontSize: 11, color: "#888" }}>Base légale indicative : {tr.base}</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                        {tools.map((toolName) => {
                          const isRC = RC_TOOLS.includes(toolName);
                          return (
                            <span
                              key={toolName}
                              style={{
                                fontSize: 10,
                                padding: "2px 8px",
                                borderRadius: 4,
                                background: isRC ? "#FAEEDA" : "#E6F1FB",
                                color: isRC ? "#854F0B" : "#185FA5",
                              }}
                            >
                              {toolName}
                              {isRC ? <span style={{ display: "inline-flex", verticalAlign: "middle", marginLeft: 4 }}><MdWarningAmber size={12} /></span> : ""}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <div style={{ display: "flex", gap: 10, marginTop: "1.5rem" }}>
              <button type="button" onClick={() => setStep(3)} style={btnBase}>
                ← Retour
              </button>
              <button type="button" onClick={() => setStep(5)} style={btnPrimary}>
                Responsables →
              </button>
            </div>
          </div>
        )}

        {/* Étape 5 responsables */}
        {step === 5 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.08em", color: "#aaa", marginBottom: 8 }}>
              Étape 5 sur {totalSteps}
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 500, marginBottom: 6 }}>Responsables</h2>
            <p style={{ fontSize: 14, color: "#666", marginBottom: "2rem", lineHeight: 1.5 }}>
              Indiquez les adresses e-mail des rôles clés. Si l&apos;e-mail existe déjà parmi vos contacts ou votre équipe plateforme, il est proposé en autocomplétion. Pour chaque adresse valide (autre que la vôtre), une{" "}
              <strong>invitation équipe</strong> sera envoyée après validation pour permettre l&apos;accès à cette organisation.
            </p>
            <div style={{ background: CARD, border: `0.5px solid ${BORDER}`, borderRadius: 12, padding: "1.25rem", marginBottom: 12 }}>
              {responsableRows.map((row, idx) => (
                <div key={row.role} style={{ marginBottom: idx === responsableRows.length - 1 ? 0 : "1rem" }}>
                  <label style={{ fontSize: 13, color: "#555", marginBottom: 6, display: "block" }}>{row.role}</label>
                  <input
                    type="email"
                    value={row.email}
                    onChange={(e) => {
                      const v = e.target.value;
                      setResponsableRows((prev) => prev.map((r, i) => (i === idx ? { ...r, email: v } : r)));
                    }}
                    placeholder="nom@entreprise.fr"
                    style={inputStyle}
                    list="hapivot-email-suggestions-rsp"
                  />
                </div>
              ))}
              <datalist id="hapivot-email-suggestions-rsp">
                {emailSuggestions.map((em) => (
                  <option key={em} value={em} />
                ))}
              </datalist>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: "1.5rem" }}>
              <button type="button" onClick={() => setStep(4)} style={btnBase}>
                ← Retour
              </button>
              <button type="button" disabled={submitting} onClick={handleFinalize} style={{ ...btnPrimary, opacity: submitting ? 0.55 : 1 }}>
                {submitting ? "Enregistrement…" : "Enregistrer et terminer ✓"}
              </button>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}
